import { router } from "@inertiajs/react"
import { beginShellNav } from "@/lib/shell-nav-pending"
import { useShotPanelStore } from "@/stores/shot-panel-store"
import { useUiStore, type AgentHandoffSurface } from "@/stores/ui-store"

/** Визит страницы: оверлей основного блока сразу, пока Inertia не ответит. */
/** Панель шота (она в AppShell) поверх текущей страницы — из упоминаний, проверки и задач. */
export function openShotPreview(projectId: number, shotId: number) {
  useShotPanelStore.getState().openShotPanel({ projectId, shotId, view: "preview" })
}

export function visitWithShellNav(href: string) {
  beginShellNav(href)
  router.visit(href)
}

export type { AgentHandoffSurface }

const ENTITY_PATH: Record<Exclude<AgentHandoffSurface, "shot" | "tasks">, string> = {
  characters: "characters",
  locations: "locations",
  props: "props",
}

const ENTITY_HASH: Record<Exclude<AgentHandoffSurface, "shot" | "tasks">, string> = {
  characters: "character",
  locations: "location",
  props: "prop",
}

export type OpenWorkspaceAgentParams = {
  projectId: number
  surface: AgentHandoffSurface
  message: string
  selectedEntityId?: number | null
  selectedShotId?: number | null
  navigate?: boolean
}

/**
 * Переход на страницу ассистента без постановки задачи в очередь автоотправки.
 * Используется плашками делегирования: задание уже создано и запущено на сервере,
 * клиенту остаётся только показать нужный экран.
 */
export function visitAgentSurface(params: {
  projectId: number
  surface: AgentHandoffSurface
  entityId?: number | null
  shotId?: number | null
}) {
  const { projectId, surface, entityId, shotId } = params
  useUiStore.getState().setWorkspaceBottomDockCollapsed(false)

  if (surface === "shot") {
    if (shotId && shotId > 0) {
      useShotPanelStore.getState().openShotPanel({ projectId, shotId, view: "preview" })
    }
    return
  }

  if (surface === "tasks") {
    useShotPanelStore.getState().close()
    const path = `/projects/${projectId}/workspace/tasks`
    if (
      typeof window !== "undefined" &&
      (window.location.pathname === path || window.location.pathname.startsWith(`${path}/`))
    ) {
      return
    }
    visitWithShellNav(path)
    return
  }

  useShotPanelStore.getState().close()

  const path = `/projects/${projectId}/workspace/${ENTITY_PATH[surface]}`
  const hash = entityId && entityId > 0 ? `#${ENTITY_HASH[surface]}-${entityId}` : ""

  if (typeof window !== "undefined" && window.location.pathname === path) {
    if (hash && window.location.hash !== hash) {
      window.history.pushState(null, "", hash)
    }
    if (entityId && entityId > 0) {
      window.dispatchEvent(
        new CustomEvent("it2tv:entity-hash", {
          detail: { surface, entityId },
        }),
      )
    }
    return
  }

  visitWithShellNav(`${path}${hash}`)
}

export function queueWorkspaceAgent(params: OpenWorkspaceAgentParams) {
  const { projectId, surface, message, selectedEntityId, selectedShotId, navigate = false } = params
  useUiStore.getState().setPendingAgentAutoSend({
    projectId,
    surface,
    message,
    ...(selectedEntityId != null && selectedEntityId > 0 ? { selectedEntityId } : {}),
    ...(selectedShotId != null && selectedShotId > 0 ? { selectedShotId } : {}),
  })
  useUiStore.getState().setWorkspaceBottomDockCollapsed(false)

  if (surface === "shot") {
    if (selectedShotId && selectedShotId > 0) {
      useShotPanelStore.getState().openShotPanel({
        projectId,
        shotId: selectedShotId,
        view: "preview",
      })
    }
    return
  }

  if (!navigate) return

  visitAgentSurface({ projectId, surface, entityId: selectedEntityId })
}
